import React, { ChangeEvent } from 'react';
import AnimateHeight from 'react-animate-height';
import { UserProfileForm } from '../../Styles/UserProfile';
import { PrimaryModernButton } from '../../../../__core/Styles/Buttons/PrimaryModernButton';
import { withTranslation, WithTranslation } from 'react-i18next';
import { changePassword } from '../../Actions/Actions';

type ChangePasswordFormProps = {
  show: boolean;
  userId: number;
  closeForm: () => void;
} & WithTranslation;

type FormValues = {
  oldPassword: string;
  newPassword: string;
  retryNewPassword: string;
};

type ChangePasswordFormState = {
  oldPasswordError: string;
  newPasswordError: string;
  values: FormValues;
};

class ChangePasswordForm extends React.Component<
  ChangePasswordFormProps,
  ChangePasswordFormState
> {
  state = {
    oldPasswordError: '',
    newPasswordError: '',
    retryNewPasswordError: '',
    values: {
      oldPassword: '',
      newPassword: '',
      retryNewPassword: '',
    },
  };

  onChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const {
      target: { value, name },
    } = event;

    this.setState(({ values }) => {
      const newValues = { ...values };
      newValues[name as keyof FormValues] = value;

      return { values: newValues };
    });
  };

  onSubmitPassword = (): void => {
    this.setState(({ values: { newPassword, retryNewPassword } }) => {
      if (newPassword !== retryNewPassword) {
        return {
          newPasswordError: 'Passwords must be the same',
          oldPasswordError: '',
        };
      } else {
        this.setNewPassword();
        return { newPasswordError: '', oldPasswordError: '' };
      }
    });
  };

  setNewPassword = async (): Promise<void> => {
    const { userId, closeForm } = this.props;

    const {
      values: { newPassword, oldPassword },
    } = this.state;

    try {
      await changePassword(userId, oldPassword, newPassword);
      this.setState({
        values: {
          newPassword: '',
          oldPassword: '',
          retryNewPassword: '',
        },
      });
      closeForm();
    } catch ({ status = null }) {
      const wrongPasswordCode = 403;
      const emptyParameters = 400;
      if (status === wrongPasswordCode) {
        this.setState({ oldPasswordError: 'Wrong password' });
      }
      if (status === emptyParameters && newPassword === '') {
        this.setState({ newPasswordError: 'Wrong password' });
      }
      if (status === emptyParameters && oldPassword === '') {
        this.setState({ oldPasswordError: 'Wrong password' });
      }
    }
  };

  render(): React.ReactElement {
    const { show, t } = this.props;
    const { values, newPasswordError, oldPasswordError } = this.state;

    return (
      <AnimateHeight duration={500} height={show ? 'auto' : 0}>
        <UserProfileForm>
          <div className="form-group">
            <input
              type="password"
              name="oldPassword"
              className={`${oldPasswordError ? 'error' : ''}`}
              placeholder={t('Old password')}
              onChange={this.onChange}
              value={values.oldPassword}
            />
            <span className="error">{t(oldPasswordError)}</span>
          </div>

          <div className="form-group">
            <input
              type="password"
              name="newPassword"
              className={`${newPasswordError ? 'error' : ''}`}
              placeholder={t('New password')}
              onChange={this.onChange}
              value={values.newPassword}
            />
            <span className="error">{t(newPasswordError)}</span>
          </div>

          <div className="form-group">
            <input
              type="password"
              name="retryNewPassword"
              className={`${newPasswordError ? 'error' : ''}`}
              placeholder={t('Retry new password')}
              onChange={this.onChange}
              value={values.retryNewPassword}
            />
            <span className="error">{t(newPasswordError)}</span>
          </div>

          <PrimaryModernButton onClick={this.onSubmitPassword}>
            {t('Save')}
          </PrimaryModernButton>
        </UserProfileForm>
      </AnimateHeight>
    );
  }
}

export default withTranslation()(ChangePasswordForm);
